import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { Banner, Card, Copyable, Empty } from '../components/ui';

interface Portal {
  tenant: { slug: string; name: string; description: string };
  saml: { id: string; name: string; idpEntityId: string; loginUrl: string; metadataUrl: string; acsUrl: string; entityId: string }[];
  oidc: { id: string; name: string; issuer: string; scopes: string; loginUrl: string; redirectUri: string }[];
  ping: { id: string; name: string; tokenHeader: string; appUrl: string; echoUrl: string }[];
}

/**
 * The public face of a tenant: whoever is testing an integration lands here and
 * launches a flow. No account required — this is the iamshowcase-style entry point.
 */
export function PortalPage() {
  const { slug = '' } = useParams();
  const [data, setData] = useState<Portal | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<Portal>(`/api/public/tenants/${slug}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load this tenant'));
  }, [slug]);

  if (error) {
    return <div className="content" style={{ maxWidth: 760, margin: '40px auto' }}><Banner kind="error">{error}</Banner></div>;
  }
  if (!data) return <div className="content"><span className="faint">Loading…</span></div>;

  const nothing = data.saml.length + data.oidc.length + data.ping.length === 0;

  return (
    <div className="content" style={{ maxWidth: 940, margin: '0 auto', paddingTop: 32 }}>
      <div className="brand" style={{ marginBottom: 22 }}>
        <span className="dot" />
        <span>
          {data.tenant.name}
          <small>IAM Testbench · {data.tenant.slug}</small>
        </span>
      </div>

      {data.tenant.description && <p className="page-sub" style={{ marginBottom: 22 }}>{data.tenant.description}</p>}

      {nothing && (
        <Empty>No enabled connections for this tenant yet. An administrator needs to configure one.</Empty>
      )}

      {data.saml.map((c) => (
        <Card
          key={c.id} title={c.name}
          hint={<>SAML 2.0 service provider{c.idpEntityId && <> · IdP <code>{c.idpEntityId}</code></>}</>}
          actions={<a className="badge proto" href={c.metadataUrl} target="_blank" rel="noreferrer">SP metadata ↗</a>}
        >
          <Copyable label="ACS URL" value={c.acsUrl} />
          <Copyable label="SP entity ID" value={c.entityId} />
          <a href={c.loginUrl}><button className="primary">Start SAML login</button></a>
        </Card>
      ))}

      {data.oidc.map((c) => (
        <Card
          key={c.id} title={c.name}
          hint={<>OpenID Connect relying party{c.issuer && <> · issuer <code>{c.issuer}</code></>} · scopes <code>{c.scopes}</code></>}
        >
          <Copyable label="Redirect URI" value={c.redirectUri} />
          <a href={c.loginUrl}><button className="primary">Start OIDC login</button></a>
        </Card>
      ))}

      {data.ping.map((c) => (
        <Card
          key={c.id} title={c.name}
          hint={<>PingAccess protected application · token header <code>{c.tokenHeader}</code></>}
        >
          <Copyable label="Protected app URL" value={c.appUrl} />
          <Copyable label="Raw header echo" value={c.echoUrl} />
          <a href={c.appUrl}><button className="primary">Open protected app</button></a>
          <p className="hint" style={{ marginTop: 12, marginBottom: 0 }}>
            Reach this through the PingAccess virtual host, not directly, or the test will
            correctly report that PingAccess was bypassed.
          </p>
        </Card>
      ))}
    </div>
  );
}
